import Autocomplete from "@mui/lab/Autocomplete";
import { MenuItem } from "@mui/material";
import TextField from "@mui/material/TextField/TextField";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAllUsers } from "../../apis/user/user-apis";

interface Props {
  onSelect?: (users: any[]) => void;
}

interface UserOption {
  label: string;
  id: number;
  phone: string;
  email: string;
  whiteLabelId: number;
  whiteLabelName: string;
}

export const NotificationsTargetUser = ({ onSelect }: Props) => {
  const whiteLabels = useSelector((s: any) => s.cities?.whiteLabels as any[]);
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    if (whiteLabels?.length) {
      setUserLoading(true);
      getAllUsers()
        .then((users) => {
          const usersByPhone = new Map<string, UserOption>();
          users.forEach((u) => {
            const phone = u.phone || "";
            if (!phone) return;
            const existing = usersByPhone.get(phone);
            if (!existing || u.id > existing.id) {
              const whiteLabelName = whiteLabels.find(
                (wl) => (wl.id || 0) === u.whiteLabelId
              )?.name;
              usersByPhone.set(phone, {
                label: u.fullName || "UNKNOWN",
                id: u.id,
                phone,
                email: u.email || "",
                whiteLabelId: u.whiteLabelId,
                whiteLabelName: whiteLabelName || String(u.whiteLabelId),
              });
            }
          });
          setAllUsers(Array.from(usersByPhone.values()));
        })
        .finally(() => setUserLoading(false));
    }
  }, [whiteLabels]);

  const filteredOptions = useMemo(() => {
    if (!inputValue) return allUsers.slice(0, 10);
    const search = inputValue.toLowerCase();
    return allUsers
      .filter(
        (u) =>
          u.label.toLowerCase().includes(search) ||
          u.email.toLowerCase().includes(search) ||
          u.phone.includes(search)
      )
      .slice(0, 10);
  }, [allUsers, inputValue]);

  return (
    <Autocomplete
      multiple={true}
      options={filteredOptions}
      groupBy={(option) => option.whiteLabelName}
      renderOption={(props, option) => (
        <MenuItem {...props}>
          <div>
            {option.label}
            <small style={{ color: "gray", marginLeft: 8 }}>
              {option.email && option.phone
                ? `${option.email} · ${option.phone}`
                : option.email || option.phone}
            </small>
          </div>
        </MenuItem>
      )}
      filterSelectedOptions={true}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => onSelect?.(value)}
      loading={userLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Users"
          placeholder="Search by name, email, or phone"
        />
      )}
    />
  );
};
